/**
 * @file PlanningControlBar.js
 * @version 1.1.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-15
 * @modified 2026-10-23
 * @description One planner at a time (hooks/usePlanningControl), on a board:
 *                - the line at the top of the board for a tab that is not
 *                  planning: who is, "Access requested from <name> — waiting
 *                  for a response (6 s)", "<name> took over planning at 10:42
 *                  because you didn't answer" with "Request control back",
 *                  "<name> is planning and said it's busy" with "Request
 *                  again";
 *                - for the planner, the prompt when someone asks: "Leave (hand
 *                  over)" or "Tell them it's busy", with the seconds left
 *                  before it goes to them anyway (the server decides when).
 *              Renders nothing before the migration, or while this tab plans
 *              and nobody is asking.
 *
 *              1.1.0 (2026-10-23): control is per board day — the words and
 *              the prompt name the day ("Matt Cole wants to plan Fri 23 Oct").
 */

import React, { useEffect, useState } from 'react';
import ConfirmDialog from './ConfirmDialog';
import { useSlideOutButton } from './SlideOutButton';
import { secondsLeft, boardDayName, dayLabel } from '../utils/planningControl';
import './PlanningControlBar.css';

const ACTION_LABEL = { request: 'Request control', 'request-again': 'Request again', take: 'Take control' };

function useTick(on) {
  const [, setN] = useState(0);
  useEffect(() => {
    if (!on) return undefined;
    const t = setInterval(() => setN(n => n + 1), 250);
    return () => clearInterval(t);
  }, [on]);
}

function PlanningControlBar({ control, board }) {
  const { view, notice, receivedAt } = control;
  const holder = control.holds;
  const incoming = holder && view && view.request ? view.request : null;
  const waiting = !!(notice && notice.waiting && view && view.request);
  useTick(!!incoming || waiting);
  const [answering, setAnswering] = useState(false);
  // "Back again" wording on the button after a hand-over or a boot.
  const actionLabel = notice && notice.action
    ? (notice.action === 'request' && view?.notice?.kind === 'lost' ? 'Request control back' : ACTION_LABEL[notice.action])
    : null;
  useSlideOutButton([actionLabel, notice?.text]);

  if (!view || !view.ready) return null;
  const left = (r) => secondsLeft(r.msLeft, receivedAt);
  const where = boardDayName(board, view);
  const day = view.perDay && view.date ? dayLabel(view.date) : '';

  const reply = async (answer) => {
    setAnswering(true);
    try { await control.answer(answer); } finally { setAnswering(false); }
  };

  return (
    <>
      {notice && (
        <div className={`planning-control-bar pcb-${notice.tone}`} role="status" aria-live="polite">
          <span className="material-symbols-rounded">{notice.waiting ? 'hourglass_top' : notice.tone === 'warn' ? 'warning' : 'group'}</span>
          <span className="pcb-text">
            {notice.text}
            {waiting && <strong className="pcb-count"> ({left(view.request)} s)</strong>}
          </span>
          {actionLabel && (
            <div className="pcb-action">
              <button type="button" className="btn-slideout" onClick={() => control.request()}>
                <div className="icon-wrapper"><span className="material-symbols-rounded">{notice.action === 'take' ? 'edit' : 'front_hand'}</span></div>
                <span className="btn-label">{actionLabel}</span>
              </button>
            </div>
          )}
        </div>
      )}
      <ConfirmDialog
        isOpen={!!incoming}
        type="warning"
        title={incoming ? `${incoming.name} wants to plan${day ? ` ${day}` : ''}` : ''}
        message={incoming ? (
          <div className="pcb-prompt">
            <p>{incoming.name} has opened {where} and wants to plan it. Your changes are already saved.</p>
            <p>Hand it over, or tell them you're busy? With no answer it goes to them in <strong>{left(incoming)} s</strong>.</p>
          </div>
        ) : null}
        confirmText={answering ? 'Handing over…' : 'Leave (hand over)'}
        confirmIcon="logout"
        cancelText="Tell them it’s busy"
        onConfirm={() => reply('leave')}
        onCancel={() => reply('busy')}
      />
    </>
  );
}

export default PlanningControlBar;
